/**
 * Trilha de participantes na sidebar da cerimônia (#91, D-063 item 1): quem já
 * assinou, quem é o atual, quem aguarda — sobre `EnvelopeView.signers[]` (a
 * autoridade é o backend; a UI só exibe, DI2-4 no espírito). Ordenação por
 * stage e depois pela ordem de cadastro — mesma semântica do gating sequencial.
 *
 * Status visual (TrailTimeline): assinado → 'done' (o Proof Lime fica reservado
 * ao evento de prova REAL — conclusão do envelope — não a cada assinatura);
 * o PRÓPRIO signatário na vez → 'active'; recusado/expirado → 'error'; resto →
 * 'pending'. Zero PII além do que a view já expõe (nome/status).
 */
import { TrailTimeline, type TrailEvent } from '@argink/ui';
import type { EnvelopeView, SignerStatus, SignerView } from '@argink/shared';
import { formatDate } from '../lib/format';

/** Rótulos NEUTROS (terceira pessoa) — `SIGNER_STATUS_LABEL` de `lib/format.ts` fala com o
 * próprio signatário ("Aguardando sua vez") e ficaria errado descrevendo os demais. */
const TRAIL_STATUS_LABEL: Record<SignerStatus, string> = {
  pending: 'Aguardando a etapa anterior',
  notified: 'Aguardando assinatura',
  in_session: 'Assinando agora',
  signed: 'Assinou',
  declined: 'Recusou',
  expired: 'Prazo expirado',
};

export interface ParticipantsTrailProps {
  envelope: EnvelopeView;
  /** id do signatário desta sessão — marcado "(você)" e 'active' quando na vez. */
  currentSignerId: string;
}

function statusOf(signer: SignerView, isCurrent: boolean): TrailEvent['status'] {
  if (signer.status === 'signed') return 'done';
  if (signer.status === 'declined' || signer.status === 'expired') return 'error';
  if (isCurrent && (signer.status === 'notified' || signer.status === 'in_session')) return 'active';
  return 'pending';
}

export function ParticipantsTrail({ envelope, currentSignerId }: ParticipantsTrailProps) {
  const ordered = [...envelope.signers].sort((a, b) => a.stage - b.stage || a.order - b.order);
  const multiStage = new Set(ordered.map((signer) => signer.stage)).size > 1;

  const events: TrailEvent[] = ordered.map((signer) => {
    const isCurrent = signer.id === currentSignerId;
    const role = signer.role === 'observer' ? ' · observador' : '';
    const stagePrefix = multiStage ? `Etapa ${signer.stage} · ` : '';
    return {
      id: signer.id,
      label: `${stagePrefix}${signer.name}${isCurrent ? ' (você)' : ''}${role}`,
      timestamp: signer.signed_at ? formatDate(signer.signed_at) : undefined,
      description: signer.role === 'observer' ? 'Acompanha, não assina' : TRAIL_STATUS_LABEL[signer.status],
      status: statusOf(signer, isCurrent),
    };
  });

  return (
    <section className="participants-trail" aria-labelledby="participants-title">
      <h2 id="participants-title" className="section__title">
        Participantes
      </h2>
      <TrailTimeline events={events} aria-label="Andamento dos participantes desta cerimônia" />
    </section>
  );
}
